// Feb-2026 · Capacitación PLD/FT — Panel del super_admin de FELVORA.
// - Crea sesiones para una entidad específica o para todas.
// - Marca "Impartida" y confirma asistencia + calificación empleado por empleado.
// - Edita datos institucionales de FELVORA (razón social, RL, RFC, domicilio).
import { useEffect, useState, useCallback } from "react";
import {
  CalendarCheck2, PlusCircle, Building2, Users, ClipboardCheck, Save,
  Loader2, ArrowRight, Trash2, Edit3, Settings2,
} from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const DIRIGIDA = ["Consejo","Dirección","Oficial de Cumplimiento","Comité","Personal de atención al público","Todo el personal"];
const TIPOS   = ["Anual obligatoria","Inducción","Actualización","Especializada"];
const MODAL   = ["Presencial","En línea","Mixta"];

const nuevoForm = () => ({
  titulo: "", entity_id: "__todas__",
  temario: "", modalidad: "En línea",
  fecha_programada: "", duracion_horas: 2,
  instructor_nombre: "", instructor_credenciales: "",
  dirigida_a: [], tipo: "Anual obligatoria",
  evaluacion_aplica: false, calificacion_minima: 80,
});

export default function CapacitacionAdmin() {
  const [tab, setTab] = useState("sesiones");
  const [sesiones, setSesiones] = useState([]);
  const [entidades, setEntidades] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(nuevoForm());
  const [saving, setSaving] = useState(false);

  const [showImpartir, setShowImpartir] = useState(false);
  const [sesionImpartir, setSesionImpartir] = useState(null);
  const [inscripciones, setInscripciones] = useState([]);
  const [resultados, setResultados] = useState({});
  const [fechaImpartida, setFechaImpartida] = useState("");
  const [impartiendo, setImpartiendo] = useState(false);

  // Config FELVORA
  const [felvoraCfg, setFelvoraCfg] = useState(null);
  const [guardandoCfg, setGuardandoCfg] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rs, re, rf] = await Promise.all([
        api.get("/capacitacion/sesiones"),
        api.get("/entidades"),
        api.get("/felvora-config"),
      ]);
      setSesiones(rs.data || []);
      setEntidades((re.data || []).sort((a,b) => (a.razon_social||"").localeCompare(b.razon_social||"")));
      setFelvoraCfg(rf.data || {});
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleDirigida = (v) => {
    const set = new Set(form.dirigida_a || []);
    if (set.has(v)) set.delete(v); else set.add(v);
    setForm({ ...form, dirigida_a: Array.from(set) });
  };
  const guardarSesion = async () => {
    if (!form.titulo.trim()) { toast.error("Título requerido."); return; }
    setSaving(true);
    try {
      const body = { ...form,
        temario: form.temario.split("\n").map((s) => s.trim()).filter(Boolean),
        duracion_horas: Number(form.duracion_horas) || 0,
        calificacion_minima: Number(form.calificacion_minima) || 80,
      };
      await api.post("/capacitacion/sesiones", body);
      toast.success("Sesión creada.");
      setShowForm(false); setForm(nuevoForm()); await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const abrirImpartir = async (s) => {
    setSesionImpartir(s);
    setFechaImpartida(new Date().toISOString().slice(0, 10));
    try {
      const { data } = await api.get(`/capacitacion/sesiones/${s.id}/inscripciones`);
      setInscripciones(data || []);
      // Inicializa resultados (asistio=true por default, calif=0)
      const r = {};
      (data || []).forEach((i) => { r[i.id] = { asistio: true, calificacion: null }; });
      setResultados(r);
      setShowImpartir(true);
    } catch (e) { toast.error(apiError(e)); }
  };
  const setRes = (iid, k, v) => setResultados((p) => ({ ...p, [iid]: { ...(p[iid] || {}), [k]: v } }));

  const confirmarImparticion = async () => {
    setImpartiendo(true);
    try {
      const payload = {
        fecha_impartida: fechaImpartida,
        resultados: Object.entries(resultados).map(([iid, r]) => ({
          inscripcion_id: iid, asistio: !!r.asistio,
          calificacion: r.calificacion === "" || r.calificacion == null ? null : Number(r.calificacion),
        })),
      };
      const { data } = await api.post(`/capacitacion/sesiones/${sesionImpartir.id}/impartir`, payload);
      toast.success(`Impartida: ${data.constancias_emitidas} constancias · ${data.reposiciones_creadas} reposiciones`);
      setShowImpartir(false); setSesionImpartir(null); setInscripciones([]);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setImpartiendo(false); }
  };

  const guardarFelvoraCfg = async () => {
    setGuardandoCfg(true);
    try {
      const { data } = await api.put("/felvora-config", felvoraCfg);
      setFelvoraCfg(data);
      toast.success("Configuración FELVORA guardada. Se aplicará a las próximas constancias.");
    } catch (e) { toast.error(apiError(e)); }
    finally { setGuardandoCfg(false); }
  };

  return (
    <div className="space-y-4" data-testid="page-capacitacion-admin">
      <PageHeader icon={<CalendarCheck2 className="h-5 w-5" />}
        title="Capacitación PLD/FT · Panel FELVORA"
        subtitle="Programa sesiones, confirma imparticiones y edita el membrete de constancias." />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="sesiones" data-testid="tab-sesiones-admin"><ClipboardCheck className="mr-1 h-4 w-4" /> Sesiones</TabsTrigger>
          <TabsTrigger value="config" data-testid="tab-config-felvora"><Settings2 className="mr-1 h-4 w-4" /> Configuración FELVORA</TabsTrigger>
        </TabsList>

        {/* ──────────── SESIONES ──────────── */}
        <TabsContent value="sesiones">
          <div className="mb-3 flex justify-end">
            <Button onClick={() => { setForm(nuevoForm()); setShowForm(true); }}
              className="bg-forest-600 hover:bg-forest-700" data-testid="btn-nueva-sesion">
              <PlusCircle className="mr-1 h-4 w-4" /> Nueva sesión
            </Button>
          </div>
          <DataTable testid="table-sesiones-admin" rows={sesiones} loading={loading}
            columns={[
              { key: "titulo", label: "Título" },
              { key: "entity_id", label: "Entidad", render: (r) => {
                if (r.entity_id === "__todas__") return <Badge className="bg-purple-100 text-purple-700">Todas</Badge>;
                const ent = entidades.find((e) => e.id === r.entity_id);
                return ent ? (ent.razon_social || ent.nombre) : r.entity_id;
              }},
              { key: "tipo", label: "Tipo" },
              { key: "fecha_programada", label: "Programada" },
              { key: "duracion_horas", label: "Horas", render: (r) => `${r.duracion_horas || 0} h` },
              { key: "estatus", label: "Estatus", render: (r) => {
                const map = { Programada: "bg-blue-100 text-blue-700", Impartida: "bg-emerald-100 text-emerald-700", Cancelada: "bg-slate-100 text-slate-500" };
                return <Badge className={map[r.estatus] || "bg-slate-100"}>{r.estatus}</Badge>;
              }},
              { key: "acciones", label: "", render: (r) => (
                r.estatus === "Programada" ? (
                  <Button size="sm" variant="outline" onClick={() => abrirImpartir(r)} data-testid={`btn-impartir-${r.id}`}>
                    <ArrowRight className="mr-1 h-3.5 w-3.5" /> Impartir
                  </Button>
                ) : null
              )},
            ]}
            emptyLabel="Aún no hay sesiones registradas."
          />
        </TabsContent>

        {/* ──────────── CONFIG FELVORA ──────────── */}
        <TabsContent value="config">
          <div className="space-y-3 rounded-lg border border-slate-200 bg-white p-5">
            <p className="text-xs text-slate-500">
              Estos datos se imprimen en cada constancia y en el programa anual. Se leen en cada emisión — cambiar el
              representante legal o el domicilio surte efecto en la siguiente constancia sin necesidad de deploy.
            </p>
            {felvoraCfg && (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div><Label>Nombre comercial</Label>
                  <Input data-testid="cfg-nombre-comercial" value={felvoraCfg.nombre_comercial || ""}
                    onChange={(e) => setFelvoraCfg({ ...felvoraCfg, nombre_comercial: e.target.value })} /></div>
                <div><Label>Razón social</Label>
                  <Input data-testid="cfg-razon-social" value={felvoraCfg.razon_social || ""}
                    onChange={(e) => setFelvoraCfg({ ...felvoraCfg, razon_social: e.target.value })} /></div>
                <div><Label>Representante legal</Label>
                  <Input data-testid="cfg-rl" value={felvoraCfg.representante_legal || ""}
                    onChange={(e) => setFelvoraCfg({ ...felvoraCfg, representante_legal: e.target.value })} /></div>
                <div><Label>Cargo del representante</Label>
                  <Input data-testid="cfg-cargo" value={felvoraCfg.cargo_representante || ""}
                    onChange={(e) => setFelvoraCfg({ ...felvoraCfg, cargo_representante: e.target.value })} /></div>
                <div><Label>RFC</Label>
                  <Input data-testid="cfg-rfc" value={felvoraCfg.rfc || ""}
                    onChange={(e) => setFelvoraCfg({ ...felvoraCfg, rfc: e.target.value })} /></div>
                <div><Label>Correo de contacto</Label>
                  <Input data-testid="cfg-correo" value={felvoraCfg.correo_contacto || ""}
                    onChange={(e) => setFelvoraCfg({ ...felvoraCfg, correo_contacto: e.target.value })} /></div>
                <div className="md:col-span-2"><Label>Domicilio fiscal</Label>
                  <Input data-testid="cfg-domicilio" value={felvoraCfg.domicilio_fiscal || ""}
                    onChange={(e) => setFelvoraCfg({ ...felvoraCfg, domicilio_fiscal: e.target.value })} /></div>
              </div>
            )}
            <div className="flex justify-end">
              <Button onClick={guardarFelvoraCfg} disabled={guardandoCfg}
                className="bg-forest-600 hover:bg-forest-700" data-testid="btn-guardar-cfg">
                {guardandoCfg ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
                Guardar
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Modal Nueva Sesión */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl" data-testid="dialog-nueva-sesion">
          <DialogHeader><DialogTitle>Nueva sesión de capacitación</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="md:col-span-2"><Label>Título *</Label>
              <Input data-testid="sesion-titulo" value={form.titulo}
                onChange={(e) => setForm({ ...form, titulo: e.target.value })} /></div>
            <div><Label>Entidad destino</Label>
              <select data-testid="sesion-entidad" className="w-full rounded border border-slate-200 px-2 py-1.5 text-sm"
                value={form.entity_id} onChange={(e) => setForm({ ...form, entity_id: e.target.value })}>
                <option value="__todas__">Todas las entidades</option>
                {entidades.map((en) => <option key={en.id} value={en.id}>{en.razon_social || en.nombre}</option>)}
              </select></div>
            <div><Label>Tipo</Label>
              <select data-testid="sesion-tipo" className="w-full rounded border border-slate-200 px-2 py-1.5 text-sm"
                value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}>
                {TIPOS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select></div>
            <div><Label>Modalidad</Label>
              <select data-testid="sesion-modalidad" className="w-full rounded border border-slate-200 px-2 py-1.5 text-sm"
                value={form.modalidad} onChange={(e) => setForm({ ...form, modalidad: e.target.value })}>
                {MODAL.map((m) => <option key={m} value={m}>{m}</option>)}
              </select></div>
            <div><Label>Fecha programada</Label>
              <Input type="date" data-testid="sesion-fecha" value={form.fecha_programada}
                onChange={(e) => setForm({ ...form, fecha_programada: e.target.value })} /></div>
            <div><Label>Duración (horas)</Label>
              <Input type="number" min="0" step="0.5" data-testid="sesion-horas" value={form.duracion_horas}
                onChange={(e) => setForm({ ...form, duracion_horas: e.target.value })} /></div>
            <div><Label>Instructor</Label>
              <Input data-testid="sesion-instructor" value={form.instructor_nombre}
                onChange={(e) => setForm({ ...form, instructor_nombre: e.target.value })} /></div>
            <div><Label>Credenciales instructor</Label>
              <Input data-testid="sesion-cred" value={form.instructor_credenciales}
                onChange={(e) => setForm({ ...form, instructor_credenciales: e.target.value })} /></div>
            <div className="md:col-span-2"><Label>Temario (un tema por línea)</Label>
              <Textarea rows={4} data-testid="sesion-temario" value={form.temario}
                onChange={(e) => setForm({ ...form, temario: e.target.value })} /></div>
            <div className="md:col-span-2">
              <Label>Dirigida a</Label>
              <div className="flex flex-wrap gap-2 pt-1">
                {DIRIGIDA.map((d) => {
                  const on = (form.dirigida_a || []).includes(d);
                  return <button key={d} type="button" onClick={() => toggleDirigida(d)}
                    className={"rounded-full border px-3 py-1 text-xs " + (on ? "border-forest-600 bg-forest-600 text-white" : "border-slate-300 bg-white text-slate-700")}
                    data-testid={`dirigida-${d.replace(/\s+/g,'-')}`}>
                    {d}
                  </button>;
                })}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch data-testid="sesion-eval-aplica" checked={form.evaluacion_aplica}
                onCheckedChange={(v) => setForm({ ...form, evaluacion_aplica: v })} />
              <Label>¿Aplica evaluación?</Label>
            </div>
            {form.evaluacion_aplica && (
              <div><Label>Calificación mínima aprobatoria</Label>
                <Input type="number" min="0" max="100" data-testid="sesion-cal-min"
                  value={form.calificacion_minima}
                  onChange={(e) => setForm({ ...form, calificacion_minima: e.target.value })} /></div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowForm(false)}>Cancelar</Button>
            <Button onClick={guardarSesion} disabled={saving}
              className="bg-forest-600 hover:bg-forest-700" data-testid="btn-guardar-sesion">
              {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <PlusCircle className="mr-1 h-4 w-4" />}
              Crear sesión
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Impartir */}
      <Dialog open={showImpartir} onOpenChange={setShowImpartir}>
        <DialogContent className="max-w-3xl" data-testid="dialog-impartir">
          <DialogHeader><DialogTitle>Confirmar impartición · {sesionImpartir?.titulo}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div><Label>Fecha de impartición</Label>
              <Input type="date" data-testid="impartir-fecha" value={fechaImpartida}
                onChange={(e) => setFechaImpartida(e.target.value)} /></div>
            <div className="text-xs text-slate-500">
              Marca asistencia y (si aplica) captura calificación. Al confirmar, se generarán constancias para
              quienes aprobaron y registros de reposición para quienes no.
              {sesionImpartir?.evaluacion_aplica && <> Calificación mínima: <b>{sesionImpartir.calificacion_minima}</b>.</>}
            </div>
          </div>
          <div className="max-h-96 space-y-1 overflow-y-auto rounded border border-slate-200 p-2">
            {inscripciones.length === 0 && <p className="text-sm text-slate-500 p-2">No hay inscritos.</p>}
            {inscripciones.map((i) => (
              <div key={i.id} className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-slate-50"
                data-testid={`res-row-${i.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium">{i.empleado_nombre}</div>
                  <div className="text-xs text-slate-500">{i.empleado_puesto || "—"} · {i.empleado_correo || "sin correo"}</div>
                </div>
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-1 text-xs">
                    <Switch data-testid={`res-asistio-${i.id}`}
                      checked={!!resultados[i.id]?.asistio}
                      onCheckedChange={(v) => setRes(i.id, "asistio", v)} />
                    Asistió
                  </label>
                  {sesionImpartir?.evaluacion_aplica && (
                    <Input type="number" min="0" max="100" placeholder="Calif"
                      className="w-20"
                      data-testid={`res-calif-${i.id}`}
                      value={resultados[i.id]?.calificacion ?? ""}
                      onChange={(e) => setRes(i.id, "calificacion", e.target.value)} />
                  )}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowImpartir(false)}>Cancelar</Button>
            <Button onClick={confirmarImparticion} disabled={impartiendo || inscripciones.length === 0}
              className="bg-forest-600 hover:bg-forest-700" data-testid="btn-confirmar-impartir">
              {impartiendo ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <ClipboardCheck className="mr-1 h-4 w-4" />}
              Confirmar impartición
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
